import { LOCATIONS, NEIGHBORHOODS, STANDALONE_LOCATION_IDS, locationById, neighborhoodById, travelClassFor } from '../../game/constants.js';

export const V2_NEIGHBORHOODS = {
  mt_gate: {
    id: 'mt_gate',
    name: 'Mt. Gate',
    shortDescription: 'Trailers, junk cars, back roads, and everybody knowing everybody else’s business.',
    thumbnail: './world/neighborhoods/mt-gate-thumb.webp',
    banner: './world/neighborhoods/mt-gate-banner.webp',
    placeholder: 'mountain-town',
    accent: 'rust',
  },
  redding: {
    id: 'redding', name: 'Redding',
    shortDescription: 'Hot streets, parks, parking lots, and just enough police presence to keep things interesting.',
    thumbnail: '', banner: '', placeholder: 'city-heat', accent: 'asphalt',
  },
  arcata: {
    id: 'arcata', name: 'Arcata',
    shortDescription: 'Fog, hippies, strange commerce, and some of the better weed and shrooms in the region.',
    thumbnail: '', banner: '', placeholder: 'coastal-fog', accent: 'coast',
  },
};

export const V2_HOME_ART = {
  dumpster: {
    id: 'dumpster',
    name: 'The Dumpster',
    thumbnail: './world/homes/dumpster-thumb.webp',
    banner: './world/homes/dumpster-banner.webp',
    placeholder: 'dumpster',
  },
};

export const V2_LOCATION_OVERRIDES = {
  home: { displayNeighborhoodId: 'mt_gate', shortName: 'Home', tags: ['SAFEHOUSE'] },
  pawn_shop: { shortName: 'Pawn Shop', tags: ['FENCE'] },
  larrens_farm: { shortName: "Larren's Farm", tags: ['SPECIAL'] },
  cavemans_house: { shortName: "Caveman Dave's", tags: ['DIVERSION'], playerSelectable: false, hiddenFromTravel: true },
  corner_store: { shortName: 'Corner Store', tags: ['STORE', 'BULLETIN'] },
  kens_house: { shortName: "Ken's House", tags: ['KEN'] },
  mikeys_place: { shortName: "Mikey's Place", tags: ['SPECIAL BUYER'] },
  robs_trailer: { shortName: "Rob's Trailer", tags: ['SMOKE SESSION'] },
  the_creek: { shortName: 'The Creek', tags: ['MARKET'] },
  mt_gate_playground: { shortName: 'The Playground', tags: ['MARKET'] },
};

export function neighborhoodMeta(id) {
  const base = neighborhoodById(id) || NEIGHBORHOODS.find((row) => row.id === id) || {};
  return { ...base, ...(V2_NEIGHBORHOODS[id] || { id, name: base.name || id, placeholder: 'generic' }) };
}

export function locationMeta(id) {
  const base = locationById(id) || LOCATIONS.find((row) => row.id === id) || { id, name: id, description: '' };
  const override = V2_LOCATION_OVERRIDES[id] || {};
  const displayNeighborhoodId = override.displayNeighborhoodId || base.neighborhoodId || '';
  return { ...base, ...override, displayNeighborhoodId, shortName: override.shortName || base.name };
}

export function homeArtForState(gameState) {
  const residenceId = String(gameState?.career?.residenceId || gameState?.career?.residence?.id || '');
  return V2_HOME_ART[residenceId] || null;
}

export function neighborhoodLocations(neighborhoodId) {
  const meta = neighborhoodMeta(neighborhoodId);
  const ids = [...(meta.locationIds || [])];
  if (neighborhoodId === 'mt_gate' && !ids.includes('home')) ids.unshift('home');
  return ids.map(locationMeta).filter(Boolean);
}

export function specialLocations() {
  return [...new Set(STANDALONE_LOCATION_IDS)]
    .map(locationMeta)
    .filter((location) => location && location.hiddenFromTravel !== true && location.playerSelectable !== false && location.isHidden !== true);
}

export function neighborhoodForPresentation(locationId) {
  const location = locationMeta(locationId);
  return location.displayNeighborhoodId ? neighborhoodMeta(location.displayNeighborhoodId) : null;
}

function careerTravelStatus(gameState) {
  return gameState?.career?.travel || gameState?.worldMap?.careerTravel || {};
}

export function travelAvailability(location, gameState, travelMode = null) {
  if (!location) return { selectable: false, hidden: true, reason: 'Unavailable' };
  if (location.hiddenFromTravel === true || location.playerSelectable === false || location.isHidden === true) {
    return { selectable: false, hidden: true, reason: 'Not a selectable destination' };
  }
  if (location.id === gameState?.currentLocationId) return { selectable: false, hidden: false, reason: 'You are already here' };

  const unlock = location.id === 'larrens_farm'
    ? Number(gameState?.serverRules?.farm_unlock_cash ?? location.unlockRequirement ?? 5000)
    : Number(location.unlockRequirement || 0);
  if (unlock > 0 && Number(gameState?.cash || 0) < unlock) {
    return { selectable: false, hidden: false, reason: `Requires $${unlock.toLocaleString()} cash` };
  }
  if (location.localFrom && gameState?.currentLocationId !== location.localFrom) {
    const source = locationMeta(location.localFrom);
    return { selectable: false, hidden: false, reason: `Enter from ${source.shortName || source.name}` };
  }

  const isCareer = gameState?.gameMode?.id === 'career' || gameState?.gameModeId === 'career';
  if (!isCareer) return { selectable: true, hidden: false, reason: '' };
  if (location.id === 'home') return { selectable: true, hidden: false, reason: '' };
  if (location.id === 'pawn_shop' && Number(gameState?.streetRep || 0) < 15) {
    return { selectable: false, hidden: false, reason: 'Need 15 Street Rep' };
  }

  const route = travelClassFor(gameState.currentLocationId, location.id);
  if (route === 'local') return { selectable: true, hidden: false, reason: '' };
  const currentNeighborhood = neighborhoodForPresentation(gameState.currentLocationId)?.id || '';
  const destinationNeighborhood = neighborhoodForPresentation(location.id)?.id || '';
  const currentWalkZone = currentNeighborhood === 'mt_gate' || ['home', 'pawn_shop'].includes(gameState.currentLocationId);
  const destinationWalkZone = destinationNeighborhood === 'mt_gate' || ['home', 'pawn_shop'].includes(location.id);
  if (currentWalkZone && destinationWalkZone) return { selectable: true, hidden: false, reason: '' };

  const status = careerTravelStatus(gameState);
  if (travelMode == null) {
    if (location.id === 'trailer_park' && status.emergencyReturnAvailable) return { selectable: true, hidden: false, reason: '' };
    if (status.chazAvailable || status.skateboard || status.bicycle) return { selectable: true, hidden: false, reason: '' };
    return { selectable: false, hidden: false, reason: 'No travel available yet' };
  }
  if (location.id === 'trailer_park' && status.emergencyReturnAvailable && travelMode === 'normal') return { selectable: true, hidden: false, reason: '' };
  if (travelMode === 'chaz' && status.chazAvailable) return { selectable: true, hidden: false, reason: '' };
  if (travelMode === 'skateboard' && status.skateboard) return { selectable: true, hidden: false, reason: '' };
  if (travelMode === 'bicycle' && status.bicycle) return { selectable: true, hidden: false, reason: '' };
  return { selectable: false, hidden: false, reason: 'That travel method is unavailable' };
}


export function locationKnowledgeStatus(location, gameState) {
  if (!location) return 'KNOWN';
  if (location.id === gameState?.currentLocationId) return 'VISITED';
  const world = gameState?.worldState || {};
  const visited = new Set([
    ...(Array.isArray(world.visitedLocations) ? world.visitedLocations : []),
    ...(Array.isArray(gameState?.visitedLocations) ? gameState.visitedLocations : []),
    ...Object.values(gameState?.marketNotebook || {}).map((entry) => entry?.locationId).filter(Boolean),
  ].map(String));
  if (visited.has(String(location.id))) return 'VISITED';
  const rumored = new Set([
    ...(Array.isArray(world.rumoredLocations) ? world.rumoredLocations : []),
    ...(Array.isArray(gameState?.rumoredLocations) ? gameState.rumoredLocations : []),
    ...(Array.isArray(world.rumors) ? world.rumors.flatMap((row) => [row?.locationId, row?.destinationId, row?.location_id, row?.destination_id]) : []),
  ].filter(Boolean).map(String));
  if (rumored.has(String(location.id))) return 'RUMORED';
  const known = new Set([
    ...(Array.isArray(world.knownLocations) ? world.knownLocations : []),
    ...(Array.isArray(gameState?.knownLocations) ? gameState.knownLocations : []),
  ].map(String));
  if (known.size && !known.has(String(location.id))) return 'NEW';
  return 'KNOWN';
}

export function locationPublicTags(location, gameState) {
  const tags = new Set(location?.tags || []);
  tags.add(locationKnowledgeStatus(location, gameState));
  if (!location) return [];
  const availability = travelAvailability(location, gameState);
  if (location.id === gameState?.currentLocationId) tags.add('YOU ARE HERE');
  if (!availability.hidden && !availability.selectable && location.id !== gameState?.currentLocationId) tags.add('LOCKED');
  if (Array.isArray(location.availableProducts) && location.availableProducts.length) tags.add('MARKET');
  if (location.id === 'corner_store' && Number(gameState?.bulletinBoard?.posts?.length || 0) > 0) tags.add('BULLETIN');
  if (location.id === 'mikeys_place' && gameState?.specialCharacter?.id === 'mikey' && gameState.specialCharacter.available === false) tags.add('NOT HOME');
  if (location.id === 'robs_trailer' && gameState?.rob?.session?.active) tags.add('SESSION ACTIVE');
  if (location.id === 'home' && gameState?.career?.homeRecovery?.active) tags.add('STUCK AT HOME');
  return [...tags].slice(0, 4);
}
